import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { maskBRLInput, numberToMaskedBRL, parseBRLInput } from '@/lib/currencyInput';

/**
 * Campo de dinheiro MASCARADO — o padrão de todo valor em R$ digitado no app.
 *
 * Os dígitos entram pela DIREITA, em centavos, e o campo se formata sozinho:
 *
 *     1 → 0,01 · 15 → 0,15 · 150 → 1,50 · 1500 → 15,00 · 150000 → 1.500,00
 *
 * É isso que mata a ambiguidade do ponto na ORIGEM (CLAUDE.md #157): em pt-BR
 * "1.500" é milhar, mas no teclado do celular "30.50" sai como decimal — os dois
 * palpites erram em silêncio, um por ÷1000 e o outro por ×100. Sem separador
 * para digitar, não há o que adivinhar.
 *
 * `maskBRLInput` (escrita), `numberToMaskedBRL` (hidratação) e `parseBRLInput`
 * (leitura) andam SEMPRE juntos: formatar de um jeito e ler de outro corrompe o
 * valor na primeira edição (a lição do #153a).
 *
 * O cursor fica preso no último dígito: tocar no meio de "1.500,00" e digitar
 * moveria a casa decimal inteira, porque a máscara relê todos os dígitos.
 */

/** Trava o cursor no fim do texto (com guarda contra laço do próprio setSelectionRange). */
export const caretToEnd = (e: React.SyntheticEvent<HTMLInputElement>) => {
  const el = e.currentTarget;
  const end = el.value.length;
  if (el.selectionStart !== end || el.selectionEnd !== end) {
    el.setSelectionRange(end, end);
  }
};

export interface MoneyInputProps {
  value: number | null | undefined;
  /** Chamado a cada tecla, para o total do documento acompanhar a digitação. */
  onCommit: (value: number) => void;
  className?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Prefixo R$ menor, para campos estreitos (a linha do item). */
  compact?: boolean;
  'aria-label'?: string;
}

export function MoneyInput({
  value,
  onCommit,
  className,
  id,
  placeholder = '0,00',
  disabled,
  compact,
  'aria-label': ariaLabel,
}: MoneyInputProps) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? numberToMaskedBRL(value);

  return (
    <MoneyFieldShell
      id={id}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      compact={compact}
      aria-label={ariaLabel}
      value={shown}
      onMaskedChange={(masked) => {
        setText(masked);
        onCommit(parseBRLInput(masked));
      }}
      onBlur={() => setText(null)}
    />
  );
}

export interface MoneyTextInputProps {
  /** Texto JÁ mascarado ("1.500,00"). Vazio = campo em branco. */
  value: string;
  /** Recebe o texto mascarado a cada tecla. */
  onChange: (masked: string) => void;
  className?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
  'aria-label'?: string;
}

/**
 * A mesma máscara e o mesmo visual do {@link MoneyInput}, para campos cujo
 * estado é TEXTO e onde o campo VAZIO tem significado próprio.
 *
 * O caso é o CUSTO (`products.cost` / `quote_items.unit_cost`): a coluna é
 * nullable de propósito e "não informado" NÃO pode virar zero — zero é um custo
 * legítimo (item doado) e transformaria ausência em margem de 100% em todo
 * relatório (#61). Com o `MoneyInput` numérico, um campo vazio comitaria `0`
 * na primeira tecla apagada, que é exatamente o erro que a coluna nullable
 * existe para impedir. Aqui o vazio continua vazio, e quem decide entre `null` e
 * número é `parseCostInput`.
 */
export function MoneyTextInput({
  value,
  onChange,
  className,
  id,
  placeholder = '0,00',
  disabled,
  compact,
  'aria-label': ariaLabel,
}: MoneyTextInputProps) {
  return (
    <MoneyFieldShell
      id={id}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      compact={compact}
      aria-label={ariaLabel}
      value={value}
      onMaskedChange={onChange}
    />
  );
}

/** O visual e o comportamento de teclado, compartilhados pelos dois campos. */
function MoneyFieldShell({
  id,
  className,
  placeholder,
  disabled,
  compact,
  value,
  onMaskedChange,
  onBlur,
  'aria-label': ariaLabel,
}: {
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
  value: string;
  onMaskedChange: (masked: string) => void;
  onBlur?: () => void;
  'aria-label'?: string;
}) {
  return (
    <div className={cn('relative', className)}>
      <span
        className={cn(
          'pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground',
          compact ? 'left-1.5 text-[10px]' : 'left-2.5 text-xs',
        )}
      >
        R$
      </span>
      <Input
        id={id}
        inputMode="numeric"
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onMaskedChange(maskBRLInput(e.target.value))}
        onFocus={caretToEnd}
        onClick={caretToEnd}
        onSelect={caretToEnd}
        onBlur={onBlur}
        className={cn(
          'h-10 w-full rounded-xl bg-background text-right',
          compact ? 'pl-6 pr-2 text-sm' : 'pl-8',
        )}
      />
    </div>
  );
}
